import {
  APIReferenceAttributes,
  ArdoqId,
  ReferenceLabelFormattingAttributes,
  FieldGlobalAttributes,
} from '@ardoq/api-types';
import { EnhancedScopeData, ReferenceLabelSource } from '@ardoq/data-model';
import { isFormattedByField, isFormattedByReferenceType } from './calculations';
import type { I18nLocale } from '@ardoq/i18n';
import { enhancedScopeDataOperations, fieldUtils } from '@ardoq/scope-data';

const getFormattingByFieldInfo = (
  referenceAttributes: APIReferenceAttributes,
  field: FieldGlobalAttributes
) => ({
  fieldLabel: field.label || field.name || '',
  fieldValue: referenceAttributes[field.name],
});

const concatenateFieldValueLabelParts = (
  fieldLabel: string,
  formattedFieldValue: string | null | undefined
) => (formattedFieldValue ? `${fieldLabel}: ${formattedFieldValue}` : null);

type GetReferenceLabelByFieldArgs = {
  referenceAttributes: APIReferenceAttributes;
  filterValue: string;
  excludeTime: boolean;
  scopeData: EnhancedScopeData;
  i18nLocale: I18nLocale;
};

const getReferenceLabelByField = ({
  referenceAttributes,
  filterValue,
  excludeTime,
  scopeData,
  i18nLocale,
}: GetReferenceLabelByFieldArgs) => {
  const field =
    scopeData.fields.find(({ name }) => name === filterValue) ?? null;
  if (!field) {
    return '';
  }

  const { fieldLabel, fieldValue } = getFormattingByFieldInfo(
    referenceAttributes,
    field
  );

  const formattedFieldValue = fieldUtils.format({
    fieldId: field._id,
    value: fieldValue,
    excludeTime,
    scopeData,
    i18nLocale,
  });

  return concatenateFieldValueLabelParts(fieldLabel, formattedFieldValue);
};

type GetLegacyReferenceDisplayLabelArgs = {
  scopeData: EnhancedScopeData;
  referenceId: ArdoqId;
  referenceLabelFormatting: ReferenceLabelFormattingAttributes | undefined;
  i18nLocale: I18nLocale;
};

/**
 * This returns the legacy format of the plain text reference label, which was used before multi-labels
 */
const getLegacyReferenceDisplayLabel = ({
  referenceLabelFormatting,
  scopeData,
  referenceId,
  i18nLocale,
}: GetLegacyReferenceDisplayLabelArgs) => {
  if (referenceLabelFormatting?.value === ReferenceLabelSource.NONE) {
    return null;
  }

  const reference = scopeData.referencesById[referenceId];
  if (!reference) return null;

  if (!referenceLabelFormatting) return reference.name ?? null;
  const filterValue = referenceLabelFormatting.value;

  if (isFormattedByField(filterValue)) {
    return getReferenceLabelByField({
      referenceAttributes: reference,
      scopeData,
      filterValue: filterValue,
      excludeTime: !referenceLabelFormatting.includeTime,
      i18nLocale,
    });
  }

  if (!isFormattedByReferenceType(filterValue, reference)) {
    return reference.displayText ?? null;
  }

  const referenceType =
    enhancedScopeDataOperations.getReferenceTypeByReferenceId(
      scopeData,
      referenceId
    );

  return referenceType?.name ?? null;
};

export const referenceLabelOperations = {
  getReferenceLabelByField,
  getLegacyReferenceDisplayLabel,
  concatenateFieldValueLabelParts,
  getFormattingByFieldInfo,
};
